import { cn } from '@/lib/utils';
import { GraduationCap, Dumbbell, Medal, Crown } from 'lucide-react';

export type SkillLevel = 'beginner' | 'amateur' | 'semi_pro' | 'professional';

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner: 'Tập chơi',
  amateur: 'Nghiệp dư',
  semi_pro: 'Bán chuyên',
  professional: 'Chuyên nghiệp',
};

const skillLevelIcons: Record<SkillLevel, React.ReactNode> = {
  beginner: <GraduationCap className="w-3 h-3" />,
  amateur: <Dumbbell className="w-3 h-3" />,
  semi_pro: <Medal className="w-3 h-3" />,
  professional: <Crown className="w-3 h-3" />,
};

const skillLevelColors: Record<SkillLevel, string> = {
  beginner: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
  amateur: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  semi_pro: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
  professional: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
};

const skillLevelIconColors: Record<SkillLevel, string> = {
  beginner: 'bg-emerald-500 text-white',
  amateur: 'bg-blue-500 text-white',
  semi_pro: 'bg-purple-500 text-white',
  professional: 'bg-amber-500 text-white',
};

interface SkillLevelBadgeProps {
  level: SkillLevel;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-3 py-1',
  lg: 'text-sm px-4 py-1.5',
};

export function SkillLevelBadge({ level, size = 'md', showLabel = true, className }: SkillLevelBadgeProps) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        skillLevelColors[level],
        sizeClasses[size],
        className
      )}
    >
      {skillLevelIcons[level]}
      {showLabel && <span>{SKILL_LEVEL_LABELS[level]}</span>}
    </div>
  );
}

// Small icon-only version for displaying next to avatars
interface SkillLevelIconProps {
  level: SkillLevel;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

const iconSizeClasses = {
  xs: 'w-3.5 h-3.5 [&>svg]:w-2 [&>svg]:h-2',
  sm: 'w-4 h-4 [&>svg]:w-2.5 [&>svg]:h-2.5',
  md: 'w-5 h-5 [&>svg]:w-3 [&>svg]:h-3',
};

export function SkillLevelIcon({ level, size = 'sm', className }: SkillLevelIconProps) {
  const IconComponent = {
    beginner: GraduationCap,
    amateur: Dumbbell,
    semi_pro: Medal,
    professional: Crown,
  }[level];

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center rounded-full',
        skillLevelIconColors[level],
        iconSizeClasses[size],
        className
      )}
      title={SKILL_LEVEL_LABELS[level]}
    >
      <IconComponent />
    </div>
  );
}
